import { useEffect, useState, useMemo } from 'react';
import { ThemeProvider as StyledComponentsThemeProvider } from 'styled-components';
import { getDefaultTheme, getThemeSync, getThemeToProvide, isSyncThemeSettings, trackTheme } from './ThemeProvider.helpers';
import devLogger from 'react-utils/devLogger';
import TrellisStyleSheetManager from '../TrellisStyleSheetManager/TrellisStyleSheetManager';
import { getLocalThemeSettings } from '../../utils/getLocalThemeSettings';
import { jsx as _jsx } from "react/jsx-runtime";
const TRELLIS_THEME_EVENT = 'trellis:theme';
let globalThemeSettings;
let initialThemePromise;
export function setupThemeSettings() {
  globalThemeSettings = getLocalThemeSettings();

  // we start this first request much before render by putting it at module scope
  if (isFullThemeSettings(globalThemeSettings)) {
    initialThemePromise = getThemeToProvide(globalThemeSettings);
  } else {
    initialThemePromise = undefined;
  }
}

// we want to get settings in module scope so that they're only ever retrieved once, and asap
setupThemeSettings();
function isFullThemeSettings(themeSettings) {
  return themeSettings.themeName !== undefined && themeSettings.themeMode !== undefined;
}
const themeDefaults = {
  themeName: 'canvas',
  themeMode: 'light'
};
function mergeThemeSettings(themeNameProp, themeModeProp, themeSettings) {
  const {
    themeName: themeNameSetting,
    themeMode: themeModeSetting
  } = themeSettings;
  return Object.assign({}, themeSettings, {
    themeName: themeNameSetting || themeNameProp || themeDefaults.themeName,
    themeMode: themeModeSetting || themeModeProp || themeDefaults.themeMode
  });
}
function isSameTheme(a, b) {
  if (!isFullThemeSettings(a) || !isFullThemeSettings(b)) {
    return false;
  }
  return a.themeName === b.themeName && a.themeMode === b.themeMode;
}

/**
 * The `ThemeProvider` provides the theme object for your app via
 * React Context. Foundations components **will not work without it**.
 * This `ThemeProvider` is used by the `AppProvider` component,
 * which is the top-level component that should wrap your entire app.
 *
 * This component is also used by `hs-test-utils` to wrap all unit test
 * component trees.
 */
export const ThemeProvider = ({
  themeName,
  themeMode,
  children,
  DO_NOT_USE_stylesheetManagerProps = {}
}) => {
  const mergedThemeSettings = useMemo(() => mergeThemeSettings(themeName, themeMode, globalThemeSettings), [themeName, themeMode]);
  const [theme, setTheme] = useState(() => getThemeSync(mergedThemeSettings));
  useEffect(() => {
    const handleUpdateTheme = themePromise => {
      themePromise.then(setTheme).catch(error => {
        devLogger.warn({
          message: `Failed to load theme with error: ${error}`,
          key: 'foundations-theming:theme-provider:theme-load-failure'
        });
        setTheme(getDefaultTheme());
      });
    };
    if (isSyncThemeSettings(mergedThemeSettings)) {
      const syncTheme = getThemeSync(mergedThemeSettings);
      setTheme(syncTheme);
    } else {
      // the initThemePromise is created with localStorage settings, so if settings are the same we can reuse it
      const themePromise = isSameTheme(mergedThemeSettings, globalThemeSettings) && initialThemePromise ? initialThemePromise : getThemeToProvide(mergedThemeSettings);
      handleUpdateTheme(themePromise);
    }
    trackTheme(mergedThemeSettings.themeName, mergedThemeSettings.themeMode);
    const handleUpdateEvent = event => {
      if (event.data.type !== TRELLIS_THEME_EVENT) {
        return;
      }
      const newThemeSettings = getLocalThemeSettings();
      const newMergedThemeSettings = mergeThemeSettings(themeName, themeMode, newThemeSettings);
      handleUpdateTheme(getThemeToProvide(newMergedThemeSettings));
    };
    window.addEventListener('message', handleUpdateEvent);
    return () => {
      window.removeEventListener('message', handleUpdateEvent);
    };
  }, [mergedThemeSettings, themeMode, themeName]);
  return /*#__PURE__*/_jsx(TrellisStyleSheetManager, {
    stylesheetManagerProps: DO_NOT_USE_stylesheetManagerProps,
    children: /*#__PURE__*/_jsx(StyledComponentsThemeProvider, {
      theme: theme,
      children: children
    })
  });
};